import styled from "@emotion/styled";
import gemURL from "assets/images/gem.svg";
import { Button, FlexRow, Text } from "atoms";
import { formatDate } from "utils/date";
import { useMarkNotificationsAsRead } from "../api/useNotifications";
import { Notification } from "../types";

function timeSince(date: number | Date) {
  const seconds = Math.floor((new Date().valueOf() - date.valueOf()) / 1000);

  let interval = seconds / 86400; // days

  if (interval > 7) {
    return null;
  }
  if (interval > 1) {
    return `${Math.floor(interval)} day${
      Math.floor(interval) !== 1 ? "s" : ""
    } ago`;
  }
  interval = seconds / 3600; // hours
  if (interval > 1) {
    return `${Math.floor(interval)} hour${
      Math.floor(interval) !== 1 ? "s" : ""
    } ago`;
  }
  interval = seconds / 60; // minutes
  if (interval > 1) {
    return `${Math.floor(interval)} minute${
      Math.floor(interval) !== 1 ? "s" : ""
    } ago`;
  }
  return "Moments ago";
}

interface NotificationContentProps {
  isDismissible?: boolean;
  notification: Notification;
  dismissToast?: () => void;
}

function NotificationContent({
  notification: { message, readAt, createdAt, notificationId },
  isDismissible,
  dismissToast,
}: NotificationContentProps) {
  const { mutate, isLoading } = useMarkNotificationsAsRead();
  const handleMarkAsRead = () => {
    mutate([notificationId]);
    if (dismissToast) dismissToast();
  };

  const notificationDate = new Date(createdAt);

  return (
    <NotificationContentWrapper
      className="NotificationContentWrapper"
      isLoading={isLoading}
    >
      <NotificationIcon src={gemURL} aria-hidden="true" />
      <SText isRead={!!readAt}>
        {message}
        <FlexRow as="span">
          <small>
            {timeSince(notificationDate)
              ? timeSince(notificationDate)
              : formatDate(createdAt, {
                  month: "long",
                  day: "numeric",
                })}
          </small>
          {isDismissible && (
            <DismissButton onClick={handleMarkAsRead} size="xxs">
              Mark as read
            </DismissButton>
          )}
        </FlexRow>
      </SText>
      {!isDismissible && <Unread isRead={!!readAt} />}
    </NotificationContentWrapper>
  );
}
const NotificationContentWrapper = styled("div")<{ isLoading: boolean }>(
  ({ isLoading }) => ({
    display: "flex",
    alignItems: "center",
    justifyContent: "flex-start",
    gap: 10,
    opacity: isLoading ? 0.3 : 1,
  })
);
const NotificationIcon = styled.img({
  flex: "0 0 20px",
  width: 20,
  height: "auto",
  pointerEvents: "none",
});
const SText = styled(Text)<{ isRead: boolean }>(({ isRead, theme }) => ({
  color: "black",
  flex: 1,
  fontSize: theme.fontSizes.xs,
  margin: 0,
  opacity: isRead ? 0.7 : 1,
  transition: ".3s opacity ease 1s",
  small: {
    fontSize: theme.fontSizes.xxxs,
    display: "block",
    opacity: 0.66,
  },
}));
const DismissButton = styled(Button)(({ theme }) => ({
  background: "transparent",
  borderRadius: 0,
  color: "black",
  opacity: 0.8,
  fontWeight: "normal",
  padding: 0,
  textDecoration: "underline",
  "&:hover": {
    background: "transparent",
    opacity: 1,
    textDecoration: "underline",
  },
}));
const Unread = styled.span<{ isRead: boolean }>(({ isRead, theme }) => ({
  opacity: isRead ? 0 : 1,
  transition: ".3s ease all 1s",
  width: 8,
  height: 8,
  backgroundColor: theme.colors.orange,
  borderRadius: theme.borderRadius.round,
  marginRight: 5,
}));
export { NotificationContent };
